import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Calendar, X, AlertTriangle, MoreVertical, RefreshCw, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useCancelSubscription, useGetPaymentOptions } from '@/lib/api/usePayment';
import { UserAPI } from '@shared/types/api';
import { PaymentModal } from '@/components/booking/PaymentModal';
import { useQueryClient } from '@tanstack/react-query';
import { pollForEntitlementUpdate } from '@/utils/exponentialBackoff';
import { useT } from '@/lib/i18n';
import { formatDate, DateFormats } from '@/utils/dateUtils';

interface EntitlementsSectionProps {
  entitlements: UserAPI.Entitlement[];
}

export default function EntitlementsSection({ entitlements }: EntitlementsSectionProps) {
  const { t } = useT();
  const cancelSubscription = useCancelSubscription();
  const getPaymentOptions = useGetPaymentOptions();
  const queryClient = useQueryClient();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [resumePaymentOptions, setResumePaymentOptions] = useState<UserAPI.PaymentOption[]>([]);
  const [resumeStartDate, setResumeStartDate] = useState<Date | null>(null);

  const handleCancelClick = (entitlement: UserAPI.Entitlement) => {
    // If the entitlement has a management URL (e.g., App Store, Play Store), open it externally
    if (entitlement.managementUrl) {
      window.open(entitlement.managementUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // Otherwise, use in-app cancellation (for Stripe subscriptions)
    setSelectedEntitlement(entitlement.id);
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedEntitlement) return;
    
    try {
      // Capture current entitlements before cancellation
      const currentEntitlements = [...entitlements];
      
      await cancelSubscription.mutateAsync({ entitlementId: selectedEntitlement });
      setCancelModalOpen(false);
      setSelectedEntitlement(null);
      
      // Poll for entitlement updates with exponential backoff
      pollForEntitlementUpdate(queryClient, currentEntitlements);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const handleResumeMembership = async () => {
    try {
      const response = await getPaymentOptions.mutateAsync({});
      if (response.success && response.data) {
        setResumePaymentOptions(response.data.paymentOptions);
        setResumeStartDate(response.data.startDate || null);
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Failed to get payment options:', error);
    }
  };

  const handlePaymentSuccess = () => {
    // Capture current entitlements before payment
    const currentEntitlements = [...entitlements];
    
    // Close modal and poll for entitlement updates with exponential backoff
    setShowPaymentModal(false);
    pollForEntitlementUpdate(queryClient, currentEntitlements);
  };

  if (entitlements.length === 0) {
    return null;
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-neutral-100">{t('entitlements.title')} 🎟️</h2>
        <div className="space-y-3">
          {entitlements.map((entitlement: UserAPI.Entitlement) => {
            const isActive = entitlement.status === 'active';
            const isCancelled = entitlement.status === 'cancelled';
            const isOneTime = entitlement.type === 'one_time';
            const now = new Date();
            const begin = entitlement.begin ? new Date(entitlement.begin) : null;
            const end = entitlement.end ? new Date(entitlement.end) : null;
            const renewsAt = entitlement.renewsAt ? new Date(entitlement.renewsAt) : null;
            
            // For subscriptions, check if it's currently active based on dates
            const isSubscriptionActive = !isOneTime && isActive && 
              (!begin || begin <= now) && 
              (!end || end >= now);
            
            // Check if subscription is set to cancel at period end
            const cancelAtPeriodEnd = !isOneTime && isActive && end && end > now;
            
            // Show cancel button only for active subscriptions not already set to cancel
            const showCancelButton = !isOneTime && isActive && !cancelAtPeriodEnd;
            
            // Show resume button for cancelled subscriptions
            const showResumeButton = !isOneTime && end;
            
            return (
              <div 
                key={entitlement.id} 
                className={``}
              >
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex items-start space-x-3">
                    {isOneTime ? (
                      <Ticket className="w-5 h-5 text-primary-500 mt-0.5" />
                    ) : (
                      <Calendar className="w-5 h-5 text-accent-500 mt-0.5" />
                    )}
                    <div>
                      <p className="font-semibold dark:text-neutral-100">
                        {isOneTime ? t('entitlements.dinnerTicket') : t('entitlements.subscription')}
                        {' '}
                        {entitlement.renewalInterval && (
                          <span className="text-primary-800 dark:text-primary-400 font-medium capitalize">
                            ({entitlement.renewalInterval})
                          </span>
                        )}
                        {isCancelled && (
                          <span className="text-amber-600 dark:text-amber-500 font-medium text-sm ml-2">
                            • {t('entitlements.cancelled')}
                          </span>
                        )}
                      </p>
                      {isOneTime && isActive && (
                        <p className="text-sm text-dark-muted dark:text-neutral-400 mt-1">
                          {t('entitlements.bookOneDinner')}
                        </p>
                      )}
                      {!isOneTime && (
                        <div className="text-sm text-dark-muted dark:text-neutral-400 mt-1 space-y-1">

                          {renewsAt && (
                            <p>{t('entitlements.renews')}: {formatDate(renewsAt, DateFormats.short)}</p>
                          )}
                          {begin && (
                            <p>{t('entitlements.started')}: {formatDate(begin, DateFormats.short)}</p>
                          )}
                          {cancelAtPeriodEnd && (
                            <p className="text-amber-500 dark:text-amber-400 font-medium">
                              {t('entitlements.cancels')}: {formatDate(end, DateFormats.short)}
                            </p>
                          )}
                          {end && !cancelAtPeriodEnd && (
                            <p>
                              {end < now ? t('entitlements.ended') : t('entitlements.ends')}: {formatDate(end, DateFormats.short)}
                            </p>
                          )}
                          {!end && isSubscriptionActive && !entitlement.renewalInterval && (
                            <p className="text-primary-500 font-medium">{t('entitlements.activeSubscription')}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {showResumeButton && (
                      <button
                        onClick={handleResumeMembership}
                        disabled={getPaymentOptions.isPending}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('entitlements.resumeMembership')}
                      </button>
                    )}
                    {showCancelButton && (
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === entitlement.id ? null : entitlement.id)}
                          className="p-1.5 rounded-full hover:bg-dark-elevated transition-colors"
                          title="More options"
                        >
                          <MoreVertical className="w-4 h-4 text-dark-muted" />
                        </button>
                        
                        {openMenuId === entitlement.id && (
                          <>
                            {/* Backdrop */}
                            <div 
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuId(null)}
                            />
                            
                            {/* Dropdown Menu */}
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -5 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -5 }}
                              className="absolute right-0 top-full mt-1 z-20 bg-white/95 border border-dark-border rounded-lg shadow-xl overflow-hidden min-w-[160px]"
                            >

                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleCancelClick(entitlement);
                                }}
                                disabled={cancelSubscription.isPending}
                                className="w-full px-4 py-2.5 text-left text-sm text-red-500 whitespace-nowrap hover:bg-red-500/10 transition-colors disabled:opacity-50 flex items-center gap-2"
                              >
                                {entitlement.managementUrl ? (
                                  <ExternalLink className="w-4 h-4" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                                {t('entitlements.cancelSubscription')}
                              </button>
                            </motion.div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Cancel Subscription Modal */}
      <AnimatePresence>
        {cancelModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setCancelModalOpen(false);
                setSelectedEntitlement(null);
              }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            
            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-dark-surface border-2 border-dark-border rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
              >
                {/* Header */}
                <div className="p-6 bg-red-500/10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/20">
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold">{t('entitlements.cancelSubscription')}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-dark-muted">
                    {t('entitlements.cancelMessage')}
                  </p>
                </div>

                {/* Actions */}
                <div className="p-6 pt-0 flex gap-3">
                  <button
                    onClick={() => {
                      setCancelModalOpen(false);
                      setSelectedEntitlement(null);
                    }}
                    disabled={cancelSubscription.isPending}
                    className="flex-1 btn btn-secondary"
                  >
                    {t('entitlements.keepSubscription')}
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    disabled={cancelSubscription.isPending}
                    className="flex-1 btn bg-red-500 hover:bg-red-600 border-red-500 disabled:opacity-50"
                  >
                    {cancelSubscription.isPending ? t('entitlements.cancelling') : t('entitlements.cancelSubscription')}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Resume Membership Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          paymentOptions={resumePaymentOptions}
          startDate={resumeStartDate}
          onSuccess={handlePaymentSuccess}
          onCancel={() => setShowPaymentModal(false)}
        />
      )}
    </>
  );
}

